import React, { useEffect, useMemo, useRef, useState } from 'react';
import SectionHeader from './SectionHeader';

const SO_SCRIPT_ID = 'scheduleonce-embed-script';
const SO_SCRIPT_SRC = 'https://cdn.oncehub.com/mergedjs/so.js';
const LOG_ALL_SCHEDULER_MESSAGES = true;

const ADVISOR_EMBEDS = {
  ben: { soPage: 'FPRBen', divId: 'SOIDIV_FPRBen' },
  tiffany: { soPage: 'FPReview', divId: 'SOIDIV_FPReview' },
  lisa: { soPage: 'LisaAngelo', divId: 'SOIDIV_LisaAngelo' },
};
const DEFAULT_ADVISOR_EMBED = ADVISOR_EMBEDS.tiffany;

function resolveAdvisorEmbed(secondaryAdvisor) {
  const key = String(secondaryAdvisor || '').trim().toLowerCase();
  return ADVISOR_EMBEDS[key] || DEFAULT_ADVISOR_EMBED;
}

function initScheduleOnceEmbed() {
  if (window.SO && typeof window.SO.reInit === 'function') {
    window.SO.reInit();
  }
}

function isAllowedOnceHubOrigin(origin) {
  try {
    const host = new URL(origin).hostname.toLowerCase();
    return host === 'oncehub.com'
      || host.endsWith('.oncehub.com')
      || host === 'onceplatform.com'
      || host.endsWith('.onceplatform.com');
  } catch (_err) {
    return false;
  }
}

function parseMessageData(raw) {
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
}

function isOnceHubBookingMessage(event, parsedData) {
  if (!isAllowedOnceHubOrigin(event.origin)) return false;

  const eventType = parsedData?.type;
  if (!eventType || typeof eventType !== 'string') return false;

  const lowered = eventType.toLowerCase();
  if (lowered === 'oncehub.booking.scheduled') return true;

  return lowered.includes('booking') || lowered.includes('confirm') || lowered.includes('scheduled');
}

function getMessageFilterDecision(event, parsedData) {
  if (!isAllowedOnceHubOrigin(event.origin)) {
    return { accepted: false, reason: 'origin_not_allowed' };
  }

  const eventType = parsedData?.type;
  if (!eventType || typeof eventType !== 'string') {
    return { accepted: false, reason: 'missing_or_invalid_type' };
  }

  const lowered = eventType.toLowerCase();
  if (lowered === 'oncehub.booking.scheduled') {
    return { accepted: true, reason: 'exact_oncehub_booking_scheduled' };
  }

  const accepted = lowered.includes('booking')
    || lowered.includes('confirm')
    || lowered.includes('scheduled');

  return {
    accepted,
    reason: accepted ? 'matched_fallback_keywords' : 'type_filtered_out',
  };
}

function formatSchedulerDateTime(rawValue) {
  if (!rawValue || typeof rawValue !== 'string') return '';

  const normalized = rawValue.trim();
  if (!normalized) return '';

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return normalized;
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch (_err) {
    return normalized;
  }
}

function setParam(url, key, value) {
  if (value !== undefined && value !== null && String(value).trim() !== '') {
    url.searchParams.set(key, String(value).trim());
  }
}

function buildSchedulerHref(baseUrl, formData, clientName, prefill) {
  const fallback = 'https://go.oncehub.com/FPReview';
  const personal = formData?.personal || {};
  const pre = prefill || {};

  const personalFullName = [personal.first_name, personal.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const prefillFullName = [pre.first_name, pre.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const fullName = personalFullName || prefillFullName || clientName || '';
  const email = personal.email || pre.email || '';
  const phone = personal.phone || pre.phone || '';

  const additionalAttendeeName = [
    personal.partner_first_name || pre.partner_first_name || pre.secondary_first_name,
    personal.partner_last_name || pre.partner_last_name || pre.secondary_last_name,
  ].filter(Boolean).join(' ').trim();
  const additionalAttendeeEmail = (
    personal.partner_email
    || pre.partner_email
    || pre.secondary_email
    || ''
  );

  try {
    const url = new URL(baseUrl || fallback);

    // Common booking-form fields
    setParam(url, 'Name', fullName);
    setParam(url, 'Email', email);
    setParam(url, 'phone', phone);
    setParam(url, 'Phone', phone);
    setParam(url, 'mobile', phone);
    setParam(url, 'Phone number', phone);

    // Custom spouse/partner fields from OnceHub (name + normalized aliases).
    setParam(url, 'partner or spouse name', additionalAttendeeName);
    setParam(url, 'customerGuests', additionalAttendeeEmail);
    setParam(url, 'secondaryEmail', additionalAttendeeEmail);

    // Force completion status to Yes for the booking form dropdown.
    setParam(url, 'Client Update Form Done', 'Yes');

    // Force RFC3986-style space encoding for compatibility with field matching.
    return url.toString().replace(/\+/g, '%20');
  } catch (_err) {
    return baseUrl || fallback;
  }
}

function getMeetingStatus(value) {
  if (!value || typeof value !== 'object') return 'not_scheduled';

  const explicitStatus = String(value.meeting_status || '').trim().toLowerCase();
  if (explicitStatus === 'canceled' || explicitStatus === 'cancelled') {
    return 'canceled';
  }
  if (explicitStatus === 'scheduled') {
    return 'scheduled';
  }

  const bookingInfo = value.booking_data?.bookingInfo;
  if (value.booking_detected) return 'scheduled';
  if (bookingInfo?.statusCode && String(bookingInfo.statusCode).trim() === '1') {
    return 'scheduled';
  }
  if (Array.isArray(bookingInfo?.MeetingTimes) && bookingInfo.MeetingTimes.length > 0) {
    return 'scheduled';
  }

  const bookedStatus = String(value.booking_data?.bookedstatus || '').trim().toLowerCase();
  if (bookedStatus === 'canceled' || bookedStatus === 'cancelled') {
    return 'canceled';
  }
  if (bookedStatus === 'scheduled') {
    return 'scheduled';
  }

  return 'not_scheduled';
}

export default function StepScheduleMeeting({
  data,
  onChange,
  schedulingUrl,
  formData,
  clientName,
  secondaryAdvisor,
  prefill,
}) {
  const value = useMemo(() => data || {}, [data]);
  const [bookingDetected, setBookingDetected] = useState(
    getMeetingStatus(value) === 'scheduled',
  );
  const mountedRef = useRef(true);
  const latestValueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const advisorEmbed = useMemo(
    () => resolveAdvisorEmbed(secondaryAdvisor),
    [secondaryAdvisor],
  );
  const schedulerHref = useMemo(
    () => buildSchedulerHref(schedulingUrl, formData, clientName, prefill),
    [schedulingUrl, formData, clientName, prefill],
  );
  const soQueryParams = useMemo(() => {
    try {
      const idx = schedulerHref.indexOf('?');
      return idx >= 0 ? schedulerHref.slice(idx + 1) : '';
    } catch (_err) {
      return '';
    }
  }, [schedulerHref]);

  useEffect(() => {
    latestValueRef.current = value;
    setBookingDetected(getMeetingStatus(value) === 'scheduled');
  }, [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  function persistBooking(bookingData) {
    const detected = bookingData?.errorStatus === 0
      || !!bookingData?.bookingInfo
      || String(bookingData?.bookedstatus || '').toLowerCase() === 'scheduled';

    setBookingDetected(detected);
    onChangeRef.current({
      ...latestValueRef.current,
      _visited: true,
      booking_detected: detected,
      meeting_status: detected ? 'scheduled' : 'not_scheduled',
      booking_data: bookingData,
    });
  }

  function markScheduledManually() {
    persistBooking({
      bookedstatus: 'scheduled',
      source: 'manual_confirm',
    });
  }
  const bookingData = value.booking_data || null;
  const meetingStatus = getMeetingStatus(value);
  const rawScheduledAt = bookingData?.bookingInfo?.MeetingTimes?.[0]?.CustomerTime
    || bookingData?.bookingInfo?.MeetingTimes?.[0]?.SOTime
    || bookingData?.bookingInfo?.MeetingTimes?.[0]?.UTCTime
    || null;
  const scheduledAt = formatSchedulerDateTime(rawScheduledAt);
  const manageRescheduleLink = bookingData?.bookingInfo?.CancelRescheduleLink || schedulerHref;

  // Mark visited on first render
  useEffect(() => {
    if (!value._visited) {
      onChange({ ...value, _visited: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register classic ScheduleOnce confirmation callback.
  useEffect(() => {
    mountedRef.current = true;

    window.SOAfterConfirmationFunction = function (bookingData) {
      if (!mountedRef.current) return;
      persistBooking(bookingData);
    };

    function handleMessage(event) {
      if (!mountedRef.current) return;
      const parsedData = parseMessageData(event.data);
      const eventType = String(parsedData?.type || '');
      const decision = getMessageFilterDecision(event, parsedData);

      if (LOG_ALL_SCHEDULER_MESSAGES) {
        console.info('[FPR Schedule] postMessage event', {
          origin: event.origin,
          eventType,
          accepted: decision.accepted,
          reason: decision.reason,
          parsedData,
          rawData: event.data,
        });
      }

      if (!decision.accepted) return;

      if (!isOnceHubBookingMessage(event, parsedData)) return;

      const loweredType = eventType.toLowerCase();
      const payload = parsedData?.payload || parsedData?.bookingInfo || null;

      if (loweredType === 'oncehub.booking.scheduled') {
        // Keep a visible breadcrumb in devtools while debugging embed callbacks.
        console.info('[FPR Schedule] oncehub.booking.scheduled received', payload);
      }

      if (!payload) return;

      // Extract the booking ID from the OnceHub payload for webhook matching.
      const bookingId = payload?.id || null;

      // Normalize payload into the same object shape used by SOAfterConfirmationFunction.
      persistBooking({
        errorStatus: 0,
        bookedstatus: 'scheduled',
        bookingInfo: payload,
        oncehub_booking_id: bookingId,  // Capture ID for backend webhook matching
      });
    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.SOAfterConfirmationFunction = undefined;
      window.removeEventListener('message', handleMessage);
      mountedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload so.js when embed params change so the classic embed picks up data-so-qry-prm.
  useEffect(() => {
    const container = document.getElementById(advisorEmbed.divId);
    if (container && soQueryParams) {
      container.setAttribute('data-so-qry-prm', soQueryParams);
    }

    const existing = document.getElementById(SO_SCRIPT_ID);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = SO_SCRIPT_ID;
    script.type = 'text/javascript';
    script.src = `${SO_SCRIPT_SRC}?v=${Date.now()}`;
    script.onload = () => initScheduleOnceEmbed();
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, [soQueryParams, advisorEmbed.divId]);

  const greetingName = (clientName || '').split(' ')[0];
  const headerTitle = meetingStatus === 'scheduled'
    ? `You're booked${greetingName ? `, ${greetingName}` : ''}!`
    : meetingStatus === 'canceled'
      ? `Your prior booking was canceled${greetingName ? `, ${greetingName}` : ''}`
    : `Welcome${greetingName ? `, ${greetingName}` : ''} — let's get your review on the calendar`;
  const headerTip = meetingStatus === 'scheduled'
    ? "Your meeting is set. Click Next to finish the rest of the questionnaire — it should take about 10–20 minutes. You can start and save your progress then come back to complete the form later if you need more time."
    : meetingStatus === 'canceled'
      ? "Your previous meeting is no longer on the calendar. Please pick a new time below before continuing."
    : "Pick a time that works for you. Once your meeting is scheduled, we'll walk through a short questionnaire to prep for it.";

  return (
    <div>
      <SectionHeader
        title={headerTitle}
        tip={headerTip}
        showAnnualNote={false}
      />

      <div className="cashflow-section-card">
        {meetingStatus === 'scheduled' ? (
          <div className="alert alert-success mb-3">
            <div className="fw-semibold mb-1">
              <i className="bi bi-check-circle me-1" />
              Meeting already scheduled
            </div>
            {scheduledAt && (
              <div className="small mb-0">Scheduled time: {scheduledAt}</div>
            )}
          </div>
        ) : meetingStatus === 'canceled' ? (
          <div className="alert alert-warning mb-3">
            <div className="fw-semibold mb-1">
              <i className="bi bi-calendar2-x me-1" />
              Previous meeting canceled
            </div>
            {scheduledAt && (
              <div className="small mb-2">Most recent canceled time: {scheduledAt}</div>
            )}
            <div className="small mb-0">Please book a new time below to continue.</div>
          </div>
        ) : (
          <>
            {/* ScheduleOnce embed START */}
            <div
              id={advisorEmbed.divId}
              data-so-page={advisorEmbed.soPage}
              data-height="550"
              data-style="border: 1px solid #d8d8d8; min-width: 290px; max-width: 900px;"
              data-psz="00"
              data-so-qry-prm={soQueryParams}
            />
            {/* ScheduleOnce embed END */}
          </>
        )}

        <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
          {bookingDetected ? (
            <div className="d-flex align-items-center gap-2">
              <a
                href={manageRescheduleLink}
                className="btn btn-outline-secondary btn-sm"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-box-arrow-up-right me-1" />
                Manage / reschedule
              </a>
              <a
                href={`mailto:torri@hffinancial.com?subject=${encodeURIComponent(`Financial Planning Review${greetingName ? ` — ${greetingName} — Question` : ''}`)}`}
                className="btn btn-outline-primary btn-sm"
              >
                <i className="bi bi-envelope me-1" />
                Contact Torri
              </a>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">
                Having trouble with the embedded scheduler?
              </span>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={markScheduledManually}
                title="Use this if you see the booking confirmation inside OnceHub."
              >
                <i className="bi bi-check2-circle me-1" />
                I&apos;ve scheduled my meeting
              </button>
              <a
                href={schedulerHref}
                className="btn btn-outline-danger btn-sm"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onChange({ ...value, _visited: true, opened_external: true })}
              >
                <i className="bi bi-box-arrow-up-right me-1" />
                Open in new tab
              </a>
              <a
                href={`mailto:torri@hffinancial.com?subject=${encodeURIComponent(`Financial Planning Review${greetingName ? ` — ${greetingName}` : ''}`)}`}
                className="btn btn-outline-primary btn-sm"
              >
                <i className="bi bi-envelope me-1" />
                Contact Torri
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
